import { useState, useEffect, useCallback } from 'react';
import { fetchWithAuth } from '../../utils/api';
import { toast } from 'sonner';
import logger from '../../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function useSharedAccess() {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedShare, setExpandedShare] = useState(null);
  const [activityLogs, setActivityLogs] = useState({});
  const [extendDays, setExtendDays] = useState({});
  const [revokeConfirm, setRevokeConfirm] = useState(null);

  const loadShares = useCallback(async () => {
    try {
      const res = await fetchWithAuth(`${BACKEND_URL}/api/portfolio/shares`);
      if (res.ok) {
        const data = await res.json();
        setShares(data.shares || []);
      }
    } catch (e) {
      toast.error('Failed to load shared access data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadShares(); }, [loadShares]);

  const loadActivity = async (shareId) => {
    try {
      const res = await fetchWithAuth(`${BACKEND_URL}/api/portfolio/shares/${shareId}/activity`);
      if (res.ok) {
        const data = await res.json();
        setActivityLogs(prev => ({ ...prev, [shareId]: data.activity || [] }));
      }
    } catch (e) {
      logger.error('Failed to fetch activity logs:', e);
    }
  };

  const toggleExpand = (shareId) => {
    if (expandedShare === shareId) {
      setExpandedShare(null);
    } else {
      setExpandedShare(shareId);
      if (!activityLogs[shareId]) loadActivity(shareId);
    }
  };

  const handleExtend = async (shareId) => {
    const days = parseInt(extendDays[shareId] || '7', 10);
    if (!days || days < 1) return;
    try {
      const res = await fetchWithAuth(`${BACKEND_URL}/api/portfolio/shares/${shareId}/extend`, {
        method: 'POST', body: JSON.stringify({ duration_days: days }),
      });
      if (res.ok) { toast.success('Access duration extended.'); loadShares(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed to extend.'); }
    } catch (e) { toast.error('Error extending access.'); }
  };

  const handleRevoke = async (shareId) => {
    try {
      const res = await fetchWithAuth(`${BACKEND_URL}/api/portfolio/shares/${shareId}`, { method: 'DELETE' });
      if (res.ok) { toast.success('Access revoked immediately.'); setRevokeConfirm(null); loadShares(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed to revoke.'); }
    } catch (e) { toast.error('Error revoking access.'); }
  };

  const formatDate = (iso) => {
    if (!iso) return '\u2014';
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getShareUrl = (token) => `${window.location.origin}/shared-portfolio?token=${token}`;

  return {
    shares, loading, expandedShare, activityLogs, extendDays, setExtendDays,
    revokeConfirm, setRevokeConfirm, toggleExpand, handleExtend, handleRevoke,
    formatDate, getShareUrl,
  };
}
